import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const reportTypes = [
  {
    id: 'weekly_sentiment',
    name: 'Weekly Brand Sentiment Report',
    description: 'Sentiment breakdown by brand for the past week',
    icon: '📊',
  },
  {
    id: 'product_feedback',
    name: 'Product Feedback Report',
    description: 'Detailed insights for each product with health scores',
    icon: '📦',
  },
  {
    id: 'marketplace',
    name: 'Marketplace Sentiment Report',
    description: 'Performance comparison across all platforms',
    icon: '🏪',
  },
];

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState('weekly_sentiment');
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);

  const generateReport = async () => {
    setGenerating(true);
    setReportData(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/reports/generate?report_type=${selectedReport}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        toast.success('Report generated successfully');
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCSV = () => {
    if (!reportData?.data) return;

    let csvContent = '';
    const data = reportData.data;

    if (Array.isArray(data) && data.length > 0) {
      // Get headers from first object
      const headers = Object.keys(data[0]).filter(k => k !== '_id');
      csvContent = headers.join(',') + '\n';

      // Add rows
      data.forEach((row) => {
        const values = headers.map((h) => {
          const val = row[h];
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val).includes(',') ? `"${val}"` : val;
        });
        csvContent += values.join(',') + '\n';
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${reportData.report_type.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV downloaded');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">
          Generate and download detailed analytics reports
        </p>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {reportTypes.map((report) => (
          <Card
            key={report.id}
            className={`card-hover cursor-pointer transition-all ${
              selectedReport === report.id
                ? 'border-[#2eb4ac] ring-1 ring-[#2eb4ac]'
                : ''
            }`}
            onClick={() => setSelectedReport(report.id)}
            data-testid={`report-type-${report.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{report.icon}</span>
                <div>
                  <h3 className="font-medium text-sm">{report.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{report.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Generate Button */}
      <div className="flex items-center gap-4">
        <Button
          onClick={generateReport}
          disabled={generating}
          className="bg-[#2eb4ac] hover:bg-[#25908a]"
          data-testid="generate-report-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Generate Report
            </>
          )}
        </Button>

        {reportData && (
          <Button variant="outline" onClick={downloadCSV} data-testid="download-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
        )}
      </div>

      {/* Report Preview */}
      {reportData && (
        <Card className="card-hover" data-testid="report-preview">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">{reportData.report_type}</CardTitle>
                <CardDescription>
                  Generated on {new Date(reportData.generated_at).toLocaleString()}
                </CardDescription>
              </div>
              <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac]">
                {Array.isArray(reportData.data) ? reportData.data.length : 0} records
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {Array.isArray(reportData.data) && reportData.data.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {Object.keys(reportData.data[0])
                        .filter((k) => k !== '_id' && !k.includes('issues'))
                        .map((key) => (
                          <th
                            key={key}
                            className="text-left py-3 px-4 font-medium text-muted-foreground"
                          >
                            {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.data.slice(0, 10).map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-border/50 hover:bg-accent/50 transition-colors"
                      >
                        {Object.entries(row)
                          .filter(([k]) => k !== '_id' && !k.includes('issues'))
                          .map(([key, value]) => (
                            <td key={key} className="py-3 px-4">
                              {typeof value === 'number'
                                ? key.includes('rating')
                                  ? value.toFixed(1)
                                  : value
                                : String(value)}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reportData.data.length > 10 && (
                  <p className="text-sm text-muted-foreground text-center mt-4">
                    Showing 10 of {reportData.data.length} records. Download CSV for full data.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No data available for this report.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
